import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import FilterMenu from './FilterMenu';

const OPTIONS = [
  { label: 'Fleet Administrator', value: 'Fleet Administrator' },
  { label: 'Manager', value: 'Manager' },
  { label: 'Member', value: 'Member' },
];

const renderMenu = (props = {}) => {
  const onApply = jest.fn();

  render(
    <ConfigProvider>
      <FilterMenu
        label="All Permissions"
        options={OPTIONS}
        title="Permissions"
        onApply={onApply}
        {...props}
      />
    </ConfigProvider>,
  );

  return { onApply };
};

const open = () =>
  fireEvent.click(screen.getByRole('button', { name: /permissions/i }));

const apply = () =>
  fireEvent.click(screen.getByRole('button', { name: /apply/i }));

const check = (label) => fireEvent.click(screen.getByText(label));

const waitForOptions = () =>
  waitFor(() => expect(screen.getByText('Member')).toBeInTheDocument());

describe('management/Users FilterMenu', () => {
  describe('the chip', () => {
    it('shows the placeholder while nothing is selected', () => {
      renderMenu();

      expect(screen.getByText('All Permissions')).toBeInTheDocument();
    });

    // Once something is chosen the chip switches to the filter's own name, and
    // KeyDS Filter renders the count beside it — so the text is split across
    // elements and the accessible name is what to assert on.
    it('switches to the title once something is selected', () => {
      renderMenu({ selected: ['Member'] });

      expect(
        screen.getByRole('button', { name: /^Permissions/ }),
      ).toBeInTheDocument();
      expect(screen.queryByText('All Permissions')).not.toBeInTheDocument();
    });

    it('shows how many are selected', () => {
      renderMenu({ selected: ['Member', 'Manager'] });

      expect(
        screen.getByRole('button', { name: /permissions/i }),
      ).toHaveTextContent('2');
    });
  });

  // Staging the selection is the point of the footer: one refetch per menu
  // rather than one per checkbox.
  describe('staging a selection', () => {
    it('reports nothing while boxes are being checked', async () => {
      const { onApply } = renderMenu();

      open();
      await waitForOptions();
      check('Member');

      expect(onApply).not.toHaveBeenCalled();
    });

    it('reports the draft on Apply', async () => {
      const { onApply } = renderMenu();

      open();
      await waitForOptions();
      check('Member');
      apply();

      await waitFor(() => expect(onApply).toHaveBeenCalledWith(['Member']));
    });

    it('accumulates several choices into one report', async () => {
      const { onApply } = renderMenu();

      open();
      await waitForOptions();
      check('Member');
      check('Manager');
      apply();

      await waitFor(() =>
        expect(onApply).toHaveBeenCalledWith(['Member', 'Manager']),
      );
    });

    it('empties the filter on Clear', async () => {
      const { onApply } = renderMenu({ selected: ['Member'] });

      open();
      await waitForOptions();
      fireEvent.click(screen.getByRole('button', { name: /clear/i }));

      await waitFor(() => expect(onApply).toHaveBeenCalledWith([]));
    });

    it('closes itself after applying', async () => {
      renderMenu();

      open();
      await waitForOptions();
      check('Member');
      apply();

      await waitFor(() =>
        expect(
          screen.queryByRole('button', { name: /apply/i }),
        ).not.toBeInTheDocument(),
      );
    });

    // Dismissing without applying must leave the applied filter alone, and
    // reopening must not show the abandoned draft.
    it('discards an abandoned draft when reopened', async () => {
      const { onApply } = renderMenu();

      open();
      await waitForOptions();
      check('Member');

      fireEvent.mouseDown(document.body);
      fireEvent.click(document.body);
      await waitFor(() =>
        expect(
          screen.queryByRole('button', { name: /apply/i }),
        ).not.toBeInTheDocument(),
      );

      open();
      await waitForOptions();

      // The tick is gone and Apply has nothing left to commit, which is the
      // draft having been reset to the applied (empty) filter.
      // Read off the row rather than through getByRole: resolving roles here
      // walks the just-dismissed panel too, and jsdom throws computing styles
      // for its detached nodes.
      expect(screen.getByText('Member').closest('li')).toHaveAttribute(
        'aria-selected',
        'false',
      );
      expect(screen.getByRole('button', { name: /apply/i })).toBeDisabled();
      expect(onApply).not.toHaveBeenCalled();
    });
  });

  // Neither button is offered while pressing it would change nothing. The two
  // ask different questions: Apply waits for the draft to diverge from what is
  // applied, Clear waits for there to be anything at all to empty.
  describe('the footer', () => {
    const clearButton = () => screen.getByRole('button', { name: /clear/i });
    const applyButton = () => screen.getByRole('button', { name: /apply/i });

    it('offers neither button while nothing is checked or applied', async () => {
      renderMenu();

      open();
      await waitForOptions();

      expect(clearButton()).toBeDisabled();
      expect(applyButton()).toBeDisabled();
    });

    it('ignores a press on the idle Apply', async () => {
      const { onApply } = renderMenu();

      open();
      await waitForOptions();
      apply();

      expect(onApply).not.toHaveBeenCalled();
    });

    it('offers both once a box is checked', async () => {
      renderMenu();

      open();
      await waitForOptions();
      check('Member');

      await waitFor(() => expect(applyButton()).toBeEnabled());
      expect(clearButton()).toBeEnabled();
    });

    // An applied menu reopened untouched still has a filter to empty, but
    // nothing new to commit.
    it('keeps Clear live and Apply idle on an untouched applied menu', async () => {
      renderMenu({ selected: ['Member'] });

      open();
      await waitForOptions();

      expect(clearButton()).toBeEnabled();
      expect(applyButton()).toBeDisabled();
    });

    it('wakes Apply when the draft gains an option', async () => {
      renderMenu({ selected: ['Member'] });

      open();
      await waitForOptions();
      check('Manager');

      await waitFor(() => expect(applyButton()).toBeEnabled());
    });

    // Unchecking the applied option is a change like any other, so Apply has
    // to be able to commit the empty result it leaves behind.
    it('wakes Apply when the draft loses its last option', async () => {
      const { onApply } = renderMenu({ selected: ['Member'] });

      open();
      await waitForOptions();
      check('Member');

      await waitFor(() => expect(applyButton()).toBeEnabled());
      apply();

      await waitFor(() => expect(onApply).toHaveBeenCalledWith([]));
    });

    it('idles Apply again once a change is undone', async () => {
      renderMenu();

      open();
      await waitForOptions();
      check('Member');
      await waitFor(() => expect(applyButton()).toBeEnabled());

      check('Member');

      await waitFor(() => expect(applyButton()).toBeDisabled());
    });

    // Clear stays live to discard a draft, but discarding one on an unfiltered
    // menu leaves the filter exactly as it was. Reporting it anyway would send
    // the list back to page one and refetch it unchanged.
    it('closes without reporting when Clear discards a draft on an unfiltered menu', async () => {
      const { onApply } = renderMenu();

      open();
      await waitForOptions();
      check('Member');

      fireEvent.click(screen.getByRole('button', { name: /clear/i }));

      await waitFor(() =>
        expect(
          screen.queryByRole('button', { name: /clear/i }),
        ).not.toBeInTheDocument(),
      );
      expect(onApply).not.toHaveBeenCalled();
    });

    // The select-all row builds the draft like any other row.
    it('wakes Apply when the All row is ticked', async () => {
      renderMenu({ allOption: true });

      open();
      await waitForOptions();
      check('All');

      await waitFor(() => expect(applyButton()).toBeEnabled());
    });
  });

  describe('the All row', () => {
    it('is absent unless asked for', async () => {
      renderMenu();

      open();
      await waitForOptions();

      expect(screen.queryByText('All')).not.toBeInTheDocument();
    });

    it('selects every option at once', async () => {
      const { onApply } = renderMenu({ allOption: true });

      open();
      await waitForOptions();
      check('All');
      apply();

      await waitFor(() =>
        expect(onApply).toHaveBeenCalledWith([
          'Fleet Administrator',
          'Manager',
          'Member',
        ]),
      );
    });

    it('clears every option when unticked', async () => {
      const { onApply } = renderMenu({
        allOption: true,
        selected: ['Fleet Administrator', 'Manager', 'Member'],
      });

      open();
      await waitForOptions();
      check('All');
      apply();

      await waitFor(() => expect(onApply).toHaveBeenCalledWith([]));
    });

    // It is a select-all toggle, not a value of its own — it must never reach
    // the caller as a filter value.
    it('never leaks its own id into the applied values', async () => {
      const { onApply } = renderMenu({ allOption: true });

      open();
      await waitForOptions();
      check('Member');
      apply();

      await waitFor(() => expect(onApply).toHaveBeenCalledWith(['Member']));
    });
  });

  describe('searching', () => {
    it('has no input unless asked for', async () => {
      renderMenu();

      open();
      await waitForOptions();

      expect(screen.queryByPlaceholderText(/search/i)).not.toBeInTheDocument();
    });

    // A bounded list narrows in the browser.
    it('narrows the options locally when no handler is given', async () => {
      renderMenu({ searchable: true, searchPlaceholder: 'Search roles' });

      open();
      await waitForOptions();
      fireEvent.change(screen.getByPlaceholderText('Search roles'), {
        target: { value: 'man' },
      });

      await waitFor(() =>
        expect(screen.queryByText('Member')).not.toBeInTheDocument(),
      );
      expect(screen.getByText('Manager')).toBeInTheDocument();
    });

    it('matches case insensitively', async () => {
      renderMenu({ searchable: true, searchPlaceholder: 'Search roles' });

      open();
      await waitForOptions();
      fireEvent.change(screen.getByPlaceholderText('Search roles'), {
        target: { value: 'MEMBER' },
      });

      await waitFor(() =>
        expect(screen.queryByText('Manager')).not.toBeInTheDocument(),
      );
      expect(screen.getByText('Member')).toBeInTheDocument();
    });

    // An unbounded list is narrowed by the server, so the options arriving are
    // already the answer and must not be filtered again.
    it('defers to the handler and leaves the options alone', async () => {
      const onSearch = jest.fn();
      renderMenu({
        searchable: true,
        searchPlaceholder: 'Search accounts',
        onSearch,
      });

      open();
      await waitForOptions();
      fireEvent.change(screen.getByPlaceholderText('Search accounts'), {
        target: { value: 'zzz' },
      });

      await waitFor(() => expect(onSearch).toHaveBeenCalledWith('zzz'));
      expect(screen.getByText('Member')).toBeInTheDocument();
    });
  });
});
